import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  BrainIcon,
  CommandIcon,
  FlaskConicalIcon,
  KeyRoundIcon,
  ListChecksIcon,
  MessageCircleIcon,
  MoonIcon,
  PlusIcon,
  RotateCcwIcon,
  SunIcon,
} from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command.js';
import {
  blockTitle,
  blockTypeLabel,
  scopeLabel,
} from '@/features/memory/memory-display.js';
import { memoryNavVisible } from '@/features/memory/memory-nav.js';
import { resetOnboarding } from '@/features/onboarding/hint-store.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { useDedicatedAutomationConversationIds } from '@/lib/api/automations.js';
import { conversationsQueryOptions } from '@/lib/api/conversations.js';
import { memoryBlocksQueryOptions } from '@/lib/api/memory.js';
import { runsQueryOptions, testSuitesQueryOptions } from '@/lib/api/quality.js';
import { runName } from '@/lib/api/quality-types.js';
import { workspaceCredentialsQueryOptions } from '@/lib/api/workspace-credentials.js';
import { workspaceQueryOptions } from '@/lib/api/workspaces.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { useTheme } from '@/ui/theme/ThemeProvider.js';

/**
 * ⌘K command palette — global navigation, theme switching, common actions, and
 * cross-section search.
 *
 * Quality entities (test suites, runs, credentials) are searchable here rather
 * than via per-section search fields: they only render once the user types, are
 * matched client-side by cmdk over the already-fetched lists, and navigate to
 * the relevant Quality screen on select.
 *
 * Workspace memory is searched here for the same reason, and this is the only
 * place it is searched from: the Memory section used to carry a search button of
 * its own, which put the same question in two places and made the answer depend
 * on which screen somebody happened to be on. A memory found here opens the
 * section on that entry — never on somebody else's personal memory, because the
 * only two documents fetched are the workspace's shared one and the caller's own.
 */
export function CommandPalette(): JSX.Element {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const navigate = useNavigate();
  const theme = useTheme();
  // Controlled query so the Quality groups can stay hidden until the user types — otherwise an empty
  // palette would dump every suite/run/credential. cmdk still does the actual fuzzy matching.
  const [query, setQuery] = useState('');
  const hasQuery = query.trim().length > 0;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  // Reset the query on any close path (Esc, backdrop, ⌘K toggle) so reopening starts fresh.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const { data: allConversations, isLoading: chatsLoading } = useQuery({
    ...conversationsQueryOptions(),
    enabled: open,
  });

  // Hide dedicated automation chats from search — they belong to the Automations section, not the
  // normal chat list (same subtraction the sidebar applies). Held back until the subtraction
  // settles, exactly as the sidebar holds it: the conversations can arrive first, and then a
  // shared dedicated chat shows here as an ordinary one for as long as that takes.
  const { ids: dedicatedConvIds, isReady: dedicatedConvIdsReady } =
    useDedicatedAutomationConversationIds();
  const conversations = dedicatedConvIdsReady
    ? allConversations?.filter((c) => !dedicatedConvIds.has(c.id))
    : undefined;

  // ── Quality sources ────────────────────────────────────────────────────────
  // Scoped to the globally-selected workspace (same as the Quality page); only rendered once a query
  // is typed so the empty palette stays the actions/chats/theme list.
  // Suites are TestRail-backed (~2s/request), so only fetch them while the user is actually
  // searching Quality — not on every palette open for actions/chat/theme navigation.
  const qualityWorkspaceId = selectedWorkspaceId ?? undefined;
  const { data: suites } = useQuery({
    ...testSuitesQueryOptions(undefined, qualityWorkspaceId),
    enabled: open && hasQuery && !!qualityWorkspaceId,
  });
  const { data: runs } = useQuery({
    ...runsQueryOptions({ scope: 'all', workspaceId: qualityWorkspaceId }),
    // The board's 5s poll is pointless for a transient palette — fetch once, only while searching.
    enabled: open && hasQuery && !!qualityWorkspaceId,
    refetchInterval: false,
  });

  // Credentials are a workspace resource (identity §6): one query for the selected workspace, only
  // once the user is actually searching, so the empty palette stays the actions/chats/theme list.
  const { data: credentials = [] } = useQuery({
    ...workspaceCredentialsQueryOptions(selectedWorkspaceId ?? ''),
    enabled: open && hasQuery && !!selectedWorkspaceId,
  });

  // ── Memory ─────────────────────────────────────────────────────────────────
  // Gated exactly as the sidebar row is: the platform flag, and the workspace's own `memory` domain,
  // which is opt-out — only an explicit `false` hides it. The workspace read is the same one the
  // sidebar already holds, so this costs no extra request.
  const { data: memoryWorkspace } = useQuery({
    ...workspaceQueryOptions(selectedWorkspaceId ?? ''),
    enabled: open && hasQuery && !!selectedWorkspaceId,
  });
  // Nothing is read until the registry has answered. The domain is opt-out, so an unresolved workspace
  // reads as visible — which is right for the sidebar ROW and wrong for a request: a keystroke would
  // fetch a workspace's memory a moment before learning the domain is switched off.
  const memorySearchable =
    getRuntimeConfig().features.memory &&
    memoryWorkspace !== undefined &&
    memoryNavVisible(memoryWorkspace.domains) &&
    open &&
    hasQuery &&
    !!selectedWorkspaceId;
  // A personal workspace has one document, so the shared read also asks what kind this one is — which is
  // answered by the same read `memorySearchable` already waited for.
  const { data: sharedMemory = [] } = useQuery({
    ...memoryBlocksQueryOptions(selectedWorkspaceId, 'shared'),
    enabled: memorySearchable && memoryWorkspace?.kind !== 'personal',
  });
  const { data: personalMemory = [] } = useQuery({
    ...memoryBlocksQueryOptions(selectedWorkspaceId, 'personal'),
    enabled: memorySearchable,
  });
  // Only `active` blocks: a superseded one is the decision the team has already replaced, and the
  // History tab is where those are read.
  const memoryBlocks = [...sharedMemory, ...personalMemory].filter(
    (block) => block.status === 'active',
  );

  // Stable keys for the 8-row skeleton placeholder rendered while the chats
  // query is in flight. Reserves the same vertical space as the final list,
  // so the dialog opens at full height and doesn't jump when data arrives.
  const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search or type a command..."
        value={query}
        // Strip leading whitespace so cmdk's matcher sees the same value `hasQuery` (trim()) tests
        // against — a stray leading space would otherwise filter everything out. Inner spaces (for
        // multi-word search) are preserved.
        onValueChange={(v) => setQuery(v.replace(/^\s+/, ''))}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Actions">
          <CommandItem
            onSelect={() => {
              setOpen(false);
              void navigate({ to: '/home' });
            }}
          >
            <PlusIcon className="size-4" strokeWidth={1.75} /> New chat
            <CommandShortcut>⌘N</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setOpen(false);
              resetOnboarding();
            }}
          >
            <RotateCcwIcon className="size-4" strokeWidth={1.75} /> Reset
            onboarding
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {chatsLoading && (
          <>
            <CommandGroup heading="Chats">
              {SKELETON_KEYS.map((k) => (
                <div
                  key={k}
                  aria-hidden
                  className="flex items-center gap-2 max-md:gap-3 rounded-md px-[10px] py-2 max-md:py-3 max-md:min-h-11"
                >
                  <div className="size-4 max-md:size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="h-3.5 max-md:h-4 w-[55%] animate-pulse rounded bg-muted" />
                </div>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {!chatsLoading && conversations && conversations.length > 0 && (
          <>
            <CommandGroup heading="Chats">
              {conversations.slice(0, 8).map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.title} ${c.id}`}
                  keywords={[c.title]}
                  onSelect={() => {
                    setOpen(false);
                    void navigate({
                      to: '/chats',
                      search: { conversationId: c.id },
                    });
                  }}
                >
                  <MessageCircleIcon className="size-4" strokeWidth={1.75} />{' '}
                  {c.title}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && suites && suites.length > 0 && (
          <>
            <CommandGroup heading="Tests">
              {suites.map((s) => (
                <CommandItem
                  key={s.id}
                  value={`${s.name} ${s.id} ${s.projectName}`}
                  keywords={[s.name, s.projectName]}
                  onSelect={() => {
                    setOpen(false);
                    void navigate({
                      to: '/quality/suites/$id',
                      params: { id: s.id },
                      search: {
                        projectId: s.projectId,
                        source: s.source,
                        sourceRef: s.sourceRef,
                      },
                    });
                  }}
                >
                  <FlaskConicalIcon
                    className="size-4 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="truncate">{s.name}</span>
                  <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                    {s.projectName}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && runs && runs.length > 0 && (
          <>
            <CommandGroup heading="Runs">
              {runs.map((run) => (
                <CommandItem
                  key={run.id}
                  value={`${runName(run)} ${run.suite ?? ''} ${run.project_name ?? ''} ${run.id}`}
                  keywords={[
                    runName(run),
                    run.suite ?? '',
                    run.project_name ?? '',
                  ]}
                  onSelect={() => {
                    setOpen(false);
                    void navigate({
                      to: '/quality/$id',
                      params: { id: run.id },
                    });
                  }}
                >
                  <ListChecksIcon
                    className="size-4 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="truncate">
                    {runName(run)}
                    {run.suite ? ` · ${run.suite}` : ''}
                  </span>
                  {run.project_name ? (
                    <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                      {run.project_name}
                    </span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && selectedWorkspaceId && credentials.length > 0 && (
          <>
            <CommandGroup heading="Credentials">
              {credentials.map((cred) => (
                <CommandItem
                  key={cred.ref}
                  value={`${cred.label} ${cred.username}`}
                  keywords={[cred.label, cred.username]}
                  onSelect={() => {
                    setOpen(false);
                    // Credentials live on the selected workspace's Credentials tab (identity §6).
                    void navigate({
                      to: '/workspaces/$id/manage',
                      params: { id: selectedWorkspaceId },
                      search: { tab: 'credentials' },
                    });
                  }}
                >
                  <KeyRoundIcon
                    className="size-4 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="truncate">
                    {cred.label}
                    <span className="text-muted-foreground">
                      {' '}
                      · {cred.username}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {memorySearchable && memoryBlocks.length > 0 && (
          <>
            <CommandGroup heading="Memory">
              {memoryBlocks.map((block) => (
                <CommandItem
                  key={block.id}
                  value={`${block.contentMarkdown} ${block.section ?? ''} ${blockTypeLabel(block.type)}`}
                  keywords={[blockTitle(block.contentMarkdown)]}
                  onSelect={() => {
                    setOpen(false);
                    void navigate({
                      to: '/memory',
                      search: {
                        tab: 'map',
                        scope: block.scope,
                        block: block.id,
                      },
                    });
                  }}
                >
                  <BrainIcon className="size-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">
                    {blockTitle(block.contentMarkdown)}
                  </span>
                  <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                    {`${blockTypeLabel(block.type)} · ${scopeLabel(block.scope)}`}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Theme">
          <CommandItem
            onSelect={() => {
              theme.setMode('light');
              setOpen(false);
            }}
          >
            <SunIcon className="size-4" strokeWidth={1.75} /> Light mode
          </CommandItem>
          <CommandItem
            onSelect={() => {
              theme.setMode('dark');
              setOpen(false);
            }}
          >
            <MoonIcon className="size-4" strokeWidth={1.75} /> Dark mode
          </CommandItem>
          <CommandItem
            onSelect={() => {
              theme.setAutoMode('system');
              setOpen(false);
            }}
          >
            <CommandIcon className="size-4" strokeWidth={1.75} /> Match system
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
